
import { useEffect, useMemo, useRef, useState } from 'react';

type CheckoutStep = 'input' | 'processing' | 'awaiting' | 'success' | 'error';
type TimeoutOutcome = 'paid' | 'unpaid';

export interface MpesaCheckoutResult {
  orderId: number;
  orderNumber: string;
  paymentId: number;
  checkoutRequestId: string;
  merchantRequestId: string;
  customerMessage: string | null;
  phone: string;
}

export interface MpesaStatusDetails {
  checkoutRequestId: string;
  orderId: number;
  paymentId: number;
  status: 'pending' | 'processing' | 'succeeded' | 'failed' | 'cancelled';
  orderStatus: string | null;
  paymentStatus: string | null;
  resultCode: number | null;
  resultDescription: string | null;
  mpesaReceipt: string | null;
  amountCents: number;
  currency: string;
  phone: string;
  transactionDate: string | null;
  updatedAt: string;
  refreshError?: string | null;
}

interface CheckoutModalProps {
  isOpen: boolean;
  method: 'mpesa' | 'card';
  total: number;
  onClose: () => void;
  onSuccess: () => void;
  isLoading?: boolean;
  onMpesaCheckout?: (phone: string) => Promise<MpesaCheckoutResult>;
  pollMpesaStatus?: (checkoutRequestId: string, options?: { refresh?: boolean }) => Promise<MpesaStatusDetails>;
}

export const CheckoutModal = ({
  isOpen,
  method,
  total,
  onClose,
  onSuccess,
  isLoading = false,
  onMpesaCheckout,
  pollMpesaStatus,
}: CheckoutModalProps) => {
  const [phone, setPhone] = useState('+254');
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<CheckoutStep>('input');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [mpesaResult, setMpesaResult] = useState<MpesaCheckoutResult | null>(null);
  const [mpesaStatus, setMpesaStatus] = useState<MpesaStatusDetails | null>(null);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [timeoutOutcome, setTimeoutOutcome] = useState<TimeoutOutcome | null>(null);
  const pollAttemptsRef = useRef(0);
  const pollingStartedAtRef = useRef<number | null>(null);
  const lastRefreshAtRef = useRef<number | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); return; }
      if (event.key !== 'Tab' || !dialog) return;
      const targets = Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]'));
      const first = targets[0], last = targets[targets.length - 1];
      if (!first) { event.preventDefault(); dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog || !dialog.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog || !dialog.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [isOpen]);

  const MPESA_POLL_INTERVAL_MS = 2000;
  const MPESA_MAX_WAIT_MS = 1 * 60 * 1000;
  const MPESA_REFRESH_MIN_MS = 3 * 1000;

  useEffect(() => {
    if (!isOpen) {
      setStep('input');
      setErrorMessage(null);
      setMpesaResult(null);
      setMpesaStatus(null);
      setPollingError(null);
      setTimedOut(false);
      setTimeoutOutcome(null);
      setPhone('+254');
      setIsProcessing(false);
      pollAttemptsRef.current = 0;
      pollingStartedAtRef.current = null;
      lastRefreshAtRef.current = null;
    }
  }, [isOpen, method]);

  const normalizedPhone = useMemo(() => phone.replace(/[^0-9+]/g, ''), [phone]);
  const phoneIsValid = normalizedPhone.replace(/\+/g, '').length >= 10;

  const handleMpesaCheckout = async () => {
    if (!onMpesaCheckout || !phoneIsValid) {
      return;
    }

    setIsProcessing(true);
    setStep('processing');
    setErrorMessage(null);
    setPollingError(null);
    setMpesaStatus(null);
    setTimedOut(false);
    setTimeoutOutcome(null);

    try {
      const result = await onMpesaCheckout(normalizedPhone);
      setMpesaResult(result);
      pollingStartedAtRef.current = Date.now();
      setStep('awaiting');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to initiate MPESA checkout right now.';
      setErrorMessage(message);
      setStep('error');
    } finally {
      setIsProcessing(false);
    }
  };

  const retry = () => {
    setStep('input');
    setIsProcessing(false);
    setErrorMessage(null);
    setMpesaResult(null);
    setMpesaStatus(null);
    setPollingError(null);
    setTimedOut(false);
    setTimeoutOutcome(null);
    pollAttemptsRef.current = 0;
    pollingStartedAtRef.current = null;
    lastRefreshAtRef.current = null;
  };

  useEffect(() => {
    if (
      step !== 'awaiting' ||
      method !== 'mpesa' ||
      !mpesaResult ||
      !pollMpesaStatus
    ) {
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const wasPaid = (status: MpesaStatusDetails | null) =>
      status?.status === 'succeeded' ||
      status?.paymentStatus === 'succeeded' ||
      status?.orderStatus === 'paid' ||
      Boolean(status?.mpesaReceipt);

    const poll = async () => {
      if (cancelled) return;
      pollAttemptsRef.current += 1;
      const now = Date.now();
      if (!pollingStartedAtRef.current) {
        pollingStartedAtRef.current = now;
      }
      const elapsed = now - pollingStartedAtRef.current;
      const canRefresh =
        !lastRefreshAtRef.current || now - lastRefreshAtRef.current >= MPESA_REFRESH_MIN_MS;
      const shouldRefresh = canRefresh && pollAttemptsRef.current % 5 === 0;
      try {
        const status = await pollMpesaStatus(mpesaResult.checkoutRequestId, shouldRefresh ? { refresh: true } : undefined);
        if (cancelled) return;
        if (shouldRefresh) {
          lastRefreshAtRef.current = now;
        }
        setMpesaStatus(status);
        setPollingError(status.refreshError ?? null);
        if (wasPaid(status)) {
          setStep('success');
          onSuccess();
          return;
        }
        const failed =
          status.status === 'failed' ||
          status.status === 'cancelled' ||
          status.paymentStatus === 'failed' ||
          status.orderStatus === 'failed';
        const hardFailure = status.status === 'cancelled' || status.resultCode === 1032;
        const timedOutNow = elapsed >= MPESA_MAX_WAIT_MS;
        if (failed || timedOutNow) {
          let finalStatus = status;
          if (canRefresh && !shouldRefresh) {
            try {
              finalStatus = await pollMpesaStatus(mpesaResult.checkoutRequestId, { refresh: true });
              if (cancelled) return;
              lastRefreshAtRef.current = Date.now();
              setMpesaStatus(finalStatus);
              setPollingError(finalStatus.refreshError ?? null);
              if (wasPaid(finalStatus)) {
                setStep('success');
                onSuccess();
                return;
              }
            } catch (error) {
              if (cancelled) return;
              setPollingError(
                error instanceof Error ? error.message : 'Unable to check payment status.'
              );
            }
          }

          if (timedOutNow) {
            const outcome: TimeoutOutcome = wasPaid(finalStatus) ? 'paid' : 'unpaid';
            setTimedOut(true);
            setTimeoutOutcome(outcome);
            setErrorMessage(
              outcome === 'paid'
                ? 'Your M-Pesa payment looks complete. Click View Library to access your purchases.'
                : 'We could not confirm your M-Pesa payment before the timeout. Please try again.'
            );
            setStep('error');
            return;
          }

          if (failed && !timedOutNow && !hardFailure) {
            timer = window.setTimeout(poll, MPESA_POLL_INTERVAL_MS);
            return;
          }

          if (failed) {
            setErrorMessage(
              finalStatus.resultDescription ?? 'The payment was not completed. Please try again.'
            );
            setStep('error');
            return;
          }
        }
      } catch (error) {
        if (cancelled) return;
        setPollingError(
          error instanceof Error ? error.message : 'Unable to check payment status.'
        );
      }
      timer = window.setTimeout(poll, MPESA_POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [step, method, mpesaResult, pollMpesaStatus, onSuccess]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="checkout-title" tabIndex={-1} className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 id="checkout-title" className="text-xl font-semibold text-gray-900">
            {method === 'mpesa' ? 'MPESA Payment' : 'Card Payment'}
          </h2>
          <button
            aria-label="Close checkout"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-line"></i>
            </div>
          </button>
        </div>

        {/* Content */}
        {step === 'input' && method === 'mpesa' && (
          <div className="space-y-4">
            <div>
              <label htmlFor="checkout-phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                id="checkout-phone"
                autoComplete="tel"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+254712345678"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-900">Total</span>
                <span className="text-xl font-bold text-gray-900">
                  KES {total.toLocaleString()}
                </span>
              </div>
            </div>
            
            <button
              onClick={handleMpesaCheckout}
              disabled={!phoneIsValid || isProcessing || isLoading}
              className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
            >
              {isProcessing ? 'Sending...' : 'Send STK Push'}
            </button>
          </div>
        )}

        {method === 'card' && <p className="text-sm text-gray-600">Card payments are unavailable. Close this dialog and choose M-Pesa.</p>}

        {step === 'processing' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 flex items-center justify-center text-emerald-600">
                <i className="ri-smartphone-line text-2xl"></i>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {method === 'mpesa' ? 'Sending STK push...' : 'Processing payment'}
              </h3>
              <p className="text-gray-600 mb-4">
                {method === 'mpesa'
                  ? 'Hold tight while we contact Safaricom.'
                  : 'Please wait while we finalize your payment.'}
              </p>
            </div>
            
            <div className="flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-emerald-600"></i>
              </div>
            </div>
          </div>
        )}

        {step === 'awaiting' && method === 'mpesa' && mpesaResult && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 flex items-center justify-center text-emerald-600">
                <i className="ri-smartphone-line text-2xl"></i>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Awaiting confirmation
              </h3>
              <p className="text-gray-600 mb-3">
                Approve the prompt sent to {mpesaResult.phone} to complete your payment.
              </p>
              {mpesaStatus?.resultDescription && (
                <p className="text-sm text-gray-500">
                  Latest update: {mpesaStatus.resultDescription}
                </p>
              )}
              {pollingError && (
                <p className="text-sm text-red-500">
                  {pollingError}
                </p>
              )}
              <div className="mt-4 text-xs text-gray-500 space-y-1">
                <p>Order #{mpesaResult.orderNumber}</p>
                <p>Checkout ID: {mpesaResult.checkoutRequestId}</p>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-emerald-600"></i>
              </div>
            </div>
          </div>
        )}

        {step === 'success' && method === 'mpesa' && mpesaResult && mpesaStatus && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 flex items-center justify-center text-emerald-600">
                <i className="ri-check-line text-2xl"></i>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Payment confirmed
              </h3>
              <p className="text-gray-600">
                Your payment was received successfully. Receipt{' '}
                {mpesaStatus.mpesaReceipt ?? 'pending allocation'}.
              </p>
              <div className="mt-4 text-sm text-gray-500 space-y-1">
                <p>Order #{mpesaResult.orderNumber}</p>
                <p>Checkout ID: {mpesaResult.checkoutRequestId}</p>
                {mpesaStatus.mpesaReceipt && <p>MPESA Receipt: {mpesaStatus.mpesaReceipt}</p>}
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        )}

        {step === 'error' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <div className="w-8 h-8 flex items-center justify-center text-red-600">
                <i className="ri-close-line text-2xl"></i>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {timedOut ? 'Payment Status' : 'Payment Failed'}
              </h3>
              <p className="text-gray-600 mb-4">
                {errorMessage ?? 'The payment was not completed. Please try again.'}
              </p>

              {timedOut && timeoutOutcome === 'paid' ? (
                <button
                  onClick={() => {
                    onSuccess();
                    onClose();
                  }}
                  className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Library
                </button>
              ) : (
                <button
                  onClick={retry}
                  className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
